import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, MapPin, Send, MessageSquare, CheckCircle2, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { api, EDX } from "@/lib/api";

export default function Contact({ onOpenPalette }: { onOpenPalette: () => void }) {
  const [form, setForm] = useState({ name:"", email:"", phone:"", company:"", subject:"", message:"" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill the required fields."); return; }
    setLoading(true);
    try { await api.post("/contact", form); toast.success("Message sent ✨"); setDone(true); }
    catch { toast.error("Something went wrong."); }
    finally { setLoading(false); }
  };

  return (
    <>
      <Navbar onOpenPalette={onOpenPalette} />
      <main className="min-h-screen pt-28 pb-20 relative page-fade" data-testid="contact-page">
        <div className="absolute inset-0 edx-aurora-bg opacity-30" />
        <div className="relative container-edx grid grid-cols-1 lg:grid-cols-12 gap-10 pt-12">
          <div className="lg:col-span-5">
            <div className="section-index">EDX.01</div>
            <h1 className="display mt-4 text-4xl md:text-5xl text-white">Let's <span className="gradient-text">talk</span>.</h1>
            <p className="mt-4 text-edx-muted max-w-md">Got a question, an idea, or just want to say hello? We'd love to hear from you.</p>
            <div className="mt-10 space-y-4">
              <ContactItem icon={<Mail size={18} />} label="General" value={EDX.emails.info} href={`mailto:${EDX.emails.info}`} color="cyan" testId="contact-info" />
              <ContactItem icon={<Mail size={18} />} label="Sales / Projects" value={EDX.emails.contact} href={`mailto:${EDX.emails.contact}`} color="pink" testId="contact-contact" />
              <ContactItem icon={<Mail size={18} />} label="Billing" value={EDX.emails.billing} href={`mailto:${EDX.emails.billing}`} color="orange" testId="contact-billing" />
              <ContactItem icon={<MapPin size={18} />} label="Office" value={EDX.location} color="cyan" testId="contact-location" />
              <ContactItem icon={<MessageSquare size={18} />} label="Chat with NOVA" value="Bottom-right · 24/7 AI assistant" color="orange" testId="contact-nova" />
            </div>
            <div className="mt-10 glass p-5">
              <div className="text-[10px] uppercase tracking-widest text-edx-muted mb-2 font-mono">Prefer to schedule?</div>
              <Link to="/book" className="btn btn-primary w-full" data-testid="contact-book-link">Book a free consultation</Link>
            </div>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                className="glass glass-edge p-12 text-center" data-testid="contact-success">
                <CheckCircle2 size={56} className="mx-auto text-edx-cyan" />
                <h2 className="display mt-5 text-3xl text-white">Message received.</h2>
                <p className="mt-3 text-edx-muted">We'll respond within 24 hours.</p>
                <button onClick={() => { setForm({ name:"", email:"", phone:"", company:"", subject:"", message:"" }); setDone(false); }}
                  className="btn btn-ghost mt-6">Send another</button>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="glass glass-edge p-6 md:p-8" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Name *"><input data-testid="contact-name" className="edx-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></Field>
                  <Field label="Email *"><input data-testid="contact-email-input" type="email" className="edx-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></Field>
                  <Field label="Phone"><input data-testid="contact-phone" className="edx-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
                  <Field label="Company"><input data-testid="contact-company" className="edx-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></Field>
                  <Field label="Subject" className="sm:col-span-2"><input data-testid="contact-subject" className="edx-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="What's this about?" /></Field>
                  <Field label="Message *" className="sm:col-span-2">
                    <textarea data-testid="contact-message" className="edx-input min-h-[160px] resize-y" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us what's on your mind…" required />
                  </Field>
                </div>
                <button type="submit" data-testid="contact-submit" disabled={loading} className="btn btn-primary mt-7 w-full sm:w-auto disabled:opacity-60">
                  {loading ? "Sending..." : "Send message"} <Send size={14} />
                </button>
              </form>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

function ContactItem({ icon, label, value, href, color, testId }: any) {
  const colors: any = { cyan: "bg-edx-cyan/15 text-edx-cyan", pink: "bg-edx-pink/15 text-edx-pink", orange: "bg-edx-orange/15 text-edx-orange" };
  const Inner = (
    <div className="glass p-5 flex items-center gap-4 hover:border-edx-cyan/40 transition-all" data-testid={testId}>
      <div className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${colors[color]}`}>{icon}</div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-edx-muted font-mono">{label}</div>
        <div className="text-sm text-white font-display font-medium">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href}>{Inner}</a> : Inner;
}

function Field({ label, children, className = "" }: any) {
  return <div className={className}><label className="text-[11px] uppercase tracking-widest text-edx-muted block mb-2 font-mono">{label}</label>{children}</div>;
}
